import { state } from '../state.js';
import * as API from '../api.js';
import { openConfirmModal, openCreateLibraryModal, openPrivacyModal, openTextPromptModal } from '../modal_manager.js';
import { UI_COPY } from '../config/uiCopy.js';
import { showToast } from './toast.js';

let _libraryPreviewPanelBound = false;
let _libraryPreviewCollapsed = true;
let _libraryCallbacks = null;
const COMPACT_STRIP_DEFAULT_GAP_PX = 6;
const COMPACT_STRIP_DEFAULT_TILE_PX = 68;

function _libraryTileCapacityLabel(count) {
    const maxTiles = Number((((state.plan || {}).entitlements || {}).tile_limits || {}).max_tiles_per_library || 0);
    const current = Number(count || 0);
    if (maxTiles > 0) {
        return `${current}/${maxTiles} tiles`;
    }
    return UI_COPY.library.tileCountLabel(current);
}

function _canDeleteLibrary(lib) {
    if (!lib) return false;
    return Boolean(lib.is_owner) || Boolean(lib.is_public);
}

function _ensureLibraryDeleteControl(item, lib) {
    if (!item || !lib) return;
    const tile = item.querySelector('.library-strip-tile');
    if (!tile) return;
    const canDelete = _canDeleteLibrary(lib);
    let delBtn = tile.querySelector('.library-delete-x');
    if (!canDelete) {
        if (delBtn) delBtn.remove();
        return;
    }
    if (!delBtn) {
        delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'source-preview-delete library-delete-x icon-btn';
        delBtn.textContent = '✕';
        tile.appendChild(delBtn);
    }
    delBtn.title = (lib.is_public && !lib.is_owner) ? 'Remove library' : 'Delete library';
    delBtn.setAttribute('aria-label', delBtn.title);
    delBtn.onclick = (e) => {
        e.stopPropagation();
        if (_libraryCallbacks && typeof _libraryCallbacks.delete === 'function') {
            _libraryCallbacks.delete(lib.id);
        }
    };
}

function _getSelectedLibraryForPreview() {
    return (state.libraries || []).find((lib) => lib.id === state.selectedLibraryId) || null;
}

function _getCompactStripCapacity(strip) {
    if (!strip) return 5;
    const width = strip.clientWidth || strip.getBoundingClientRect().width || 0;
    if (!Number.isFinite(width) || width <= 0) return 5;
    const styles = window.getComputedStyle(strip);
    const gap = parseFloat(styles.columnGap || styles.gap || '') || COMPACT_STRIP_DEFAULT_GAP_PX;
    const tileSize = COMPACT_STRIP_DEFAULT_TILE_PX;
    const safeWidth = Math.max(0, width - 0.5);
    return Math.max(1, Math.floor((safeWidth + gap) / (tileSize + gap)));
}

function _appendCompactMoreTile(strip, hiddenCount, label) {
    if (!strip || hiddenCount <= 0) return;
    const moreTile = document.createElement('div');
    moreTile.className = 'history-tile history-more-indicator';
    moreTile.title = `${hiddenCount} more ${label}`;
    moreTile.textContent = `+${hiddenCount}`;
    strip.appendChild(moreTile);
}

function _previewStatusMessage({ tileCount = 0, loading = false, error = '' } = {}) {
    const safeCount = Math.max(0, Number(tileCount || 0));
    if (loading) {
        return safeCount > 0
            ? `Loading thumbnails for ${safeCount} tiles...`
            : 'Loading thumbnails...';
    }
    if (error) {
        return error;
    }
    return safeCount > 0
        ? `Click to explore ${_libraryTileCapacityLabel(safeCount)}.`
        : 'No tiles available.';
}

function _renderLibraryPreviewCollapsedSummary({
    tileCount = 0,
    images = [],
    loading = false,
    error = '',
} = {}) {
    const summary = document.getElementById('libPreviewCollapsedSummary');
    const strip = document.getElementById('libPreviewCollapsedStrip');
    if (!summary || !strip) return;

    const safeCount = Math.max(0, Number(tileCount || 0));
    const safeImages = (Array.isArray(images) ? images : []).filter((url) => String(url || '').trim());
    const message = _previewStatusMessage({ tileCount: safeCount, loading, error });

    strip.innerHTML = '';
    summary.classList.toggle('is-disabled', safeCount <= 0);
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-controls', 'libPreviewBody');
    summary.setAttribute('aria-label', message);
    summary.tabIndex = safeCount > 0 ? 0 : -1;
    summary.title = message;

    if (!safeImages.length) {
        const copy = document.createElement('div');
        copy.className = 'library-preview-collapsed-copy';
        copy.textContent = message;
        strip.appendChild(copy);
        return;
    }

    const capacity = _getCompactStripCapacity(strip);
    const reserveMoreTile = safeCount > capacity;
    const previewSlots = reserveMoreTile ? Math.max(0, capacity - 1) : Math.min(safeCount, capacity);
    let visibleCount = Math.max(0, Math.min(safeImages.length, previewSlots));

    const renderWithVisibleCount = (count) => {
        strip.innerHTML = '';
        safeImages.slice(0, count).forEach((url) => {
            const tile = document.createElement('div');
            tile.className = 'history-tile';

            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.src = url;
            img.alt = 'Library tile preview';

            tile.appendChild(img);
            strip.appendChild(tile);
        });

        const hiddenCount = Math.max(0, safeCount - count);
        _appendCompactMoreTile(strip, hiddenCount, 'tiles');
        return hiddenCount;
    };

    let hiddenCount = renderWithVisibleCount(visibleCount);
    while (visibleCount > 0 && strip.scrollWidth > (strip.clientWidth + 0.5)) {
        visibleCount -= 1;
        hiddenCount = renderWithVisibleCount(visibleCount);
    }

    if (visibleCount === 0 && hiddenCount === 0) {
        const placeholder = document.createElement('span');
        placeholder.className = 'collapsed-summary-thumb-placeholder';
        strip.appendChild(placeholder);
    }
}

function _ensureLibraryPreviewFeedback(grid) {
    const body = document.getElementById('libPreviewBody');
    if (!body || !grid) return null;
    let feedback = document.getElementById('libPreviewFeedback');
    if (!feedback) {
        feedback = document.createElement('div');
        feedback.id = 'libPreviewFeedback';
        feedback.className = 'library-preview-feedback hidden';
        body.insertBefore(feedback, grid);
    }
    return feedback;
}

function _setLibraryPreviewFeedback({
    grid,
    tileCount = 0,
    loading = false,
    error = '',
} = {}) {
    const feedback = _ensureLibraryPreviewFeedback(grid);
    if (!feedback) return;

    const safeError = String(error || '').trim();
    if (!loading && !safeError) {
        feedback.textContent = '';
        feedback.className = 'library-preview-feedback hidden';
        return;
    }

    feedback.textContent = _previewStatusMessage({
        tileCount,
        loading,
        error: safeError,
    });
    feedback.className = `library-preview-feedback${safeError ? ' is-error' : ''}`;
}

function _renderLibraryPreviewGridMessage(grid, message, kind = 'info') {
    if (!grid) return;
    grid.innerHTML = '';
    const note = document.createElement('div');
    note.className = `library-preview-grid-message${kind === 'error' ? ' is-error' : ''}`;
    note.textContent = message;
    grid.appendChild(note);
}

function _logLibraryPreviewInteraction(action, trigger) {
    const selected = _getSelectedLibraryForPreview();
    API.logUiFlow('library_explorer', action, 'ok', {
        trigger: String(trigger || 'unknown'),
        library_id: String(state.selectedLibraryId || ''),
        tile_count: Number((selected && selected.tile_count) || 0),
        preview_count: Array.isArray(selected && selected.previews) ? selected.previews.length : 0,
    }).catch(() => {});
}

function _setLibraryPreviewCollapsed(collapsed) {
    _libraryPreviewCollapsed = Boolean(collapsed);
    const area = document.getElementById('libPreviewArea');
    const body = document.getElementById('libPreviewBody');
    const toggle = document.getElementById('libPreviewToggle');
    const summary = document.getElementById('libPreviewCollapsedSummary');
    if (area) area.classList.toggle('is-collapsed', _libraryPreviewCollapsed);
    if (body) body.classList.toggle('hidden', _libraryPreviewCollapsed);
    if (summary) summary.classList.toggle('hidden', !_libraryPreviewCollapsed);
    if (toggle) toggle.setAttribute('aria-expanded', String(!_libraryPreviewCollapsed));
    document.dispatchEvent(new CustomEvent('library:preview-collapsed-changed', {
        detail: { collapsed: _libraryPreviewCollapsed },
    }));
}

function _ensureLibraryPreviewPanelEvents() {
    if (_libraryPreviewPanelBound) return;
    const area = document.getElementById('libPreviewArea');
    const toggle = document.getElementById('libPreviewToggle');
    const summary = document.getElementById('libPreviewCollapsedSummary');
    if (!area || !toggle || !summary) return;

    toggle.addEventListener('click', () => {
        const nextCollapsed = !_libraryPreviewCollapsed;
        _setLibraryPreviewCollapsed(nextCollapsed);
        _logLibraryPreviewInteraction(nextCollapsed ? 'collapse' : 'expand', 'toggle');
    });

    const openPreviewFromSummary = () => {
        if (!_libraryPreviewCollapsed || summary.classList.contains('is-disabled')) return;
        _setLibraryPreviewCollapsed(false);
        _logLibraryPreviewInteraction('expand', 'summary');
    };

    summary.addEventListener('click', (event) => {
        const target = event.target;
        if (
            target &&
            typeof target.closest === 'function' &&
            target.closest('button, a, input, select, textarea, label')
        ) {
            return;
        }
        openPreviewFromSummary();
    });
    summary.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPreviewFromSummary();
        }
    });

    _libraryPreviewPanelBound = true;
    _setLibraryPreviewCollapsed(true);
}

function _buildThumbGrid(tile, previews) {
    const grid = document.createElement('div');
    grid.className = 'library-strip-bg-grid';
    const values = Array.isArray(previews) ? previews.slice(0, 9) : [];
    for (let i = 0; i < 9; i++) {
        const cell = document.createElement('div');
        cell.className = 'library-strip-bg-cell';
        const src = values[i] || '';
        if (src) cell.style.backgroundImage = `url('${src}')`;
        grid.appendChild(cell);
    }
    tile.appendChild(grid);
}

function _buildCreateLibraryItem(selectCallback) {
    const createItem = document.createElement('div');
    createItem.className = "trivia-thumb library-strip-item library-strip-create";
    createItem.setAttribute('role', 'button');
    createItem.tabIndex = 0;

    const createTile = document.createElement('div');
    createTile.className = "history-tile library-strip-tile";
    createTile.innerHTML = `
        <div class="library-create-body">
            <div class="library-create-plus">+</div>
            <div>${UI_COPY.library.newLibraryLabel}</div>
        </div>
    `;

    const createMeta = document.createElement('div');
    createMeta.className = 'meta';
    createMeta.textContent = 'Create new';
    createMeta.title = 'Create new library';

    createItem.appendChild(createTile);
    createItem.appendChild(createMeta);
    const openCreate = async () => {
        const res = await openCreateLibraryModal();
        if (res && res.name && typeof selectCallback === 'function') {
            selectCallback(null, res.name, res.isPublic);
        }
    };
    createItem.onclick = openCreate;
    createItem.onkeydown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openCreate();
        }
    };
    return createItem;
}

function _ensureCreateLibraryItem(libraries) {
    const grid = document.getElementById('libGrid');
    if (!grid) return;
    const libs = Array.isArray(libraries) ? libraries : (state.libraries || []);
    const shouldShow = Boolean(state.selectedLibraryId) && libs.length > 0;
    const existing = grid.querySelector('.library-strip-create');
    if (!shouldShow) {
        if (existing) existing.remove();
        return;
    }
    if (existing) return;
    if (!_libraryCallbacks || typeof _libraryCallbacks.select !== 'function') return;
    const createItem = _buildCreateLibraryItem(_libraryCallbacks.select);
    grid.insertBefore(createItem, grid.firstChild);
}

function _applyLibraryStripItemContent(item, lib) {
    const tile = item.querySelector('.library-strip-tile');
    const badge = item.querySelector('.history-date');
    const name = item.querySelector('.name');
    if (!tile || !badge || !name) return;

    const existingGrid = tile.querySelector('.library-strip-bg-grid');
    if (existingGrid) existingGrid.remove();
    _buildThumbGrid(tile, lib.previews || []);
    badge.textContent = _libraryTileCapacityLabel(lib.tile_count || 0);
    name.textContent = lib.name || '';
    name.title = name.textContent;
    const existingBadge = tile.querySelector('.library-public-badge');
    if (existingBadge) existingBadge.remove();
    if (lib.is_public) {
        const publicBadge = document.createElement('div');
        publicBadge.className = 'library-public-badge';
        publicBadge.textContent = '🌍';
        publicBadge.title = 'Public library';
        tile.appendChild(publicBadge);
    }
    _ensureLibraryDeleteControl(item, lib);
}

export function renderLibraryGrid(libs, selectCallback, deleteCallback, renameCallback, changePrivacyCallback) {
    const grid = document.getElementById('libGrid');
    const empty = document.getElementById('libGridEmpty');
    if (!grid) return;
    grid.innerHTML = "";
    _libraryCallbacks = {
        select: selectCallback,
        delete: deleteCallback,
        rename: renameCallback,
        privacy: changePrivacyCallback,
    };

    const libraries = Array.isArray(libs) ? libs : [];
    if (empty) {
        empty.classList.toggle('hidden', libraries.length > 0);
    }

    _ensureCreateLibraryItem(libraries);

    libraries.forEach(lib => {
        const item = document.createElement('div');
        item.className = 'trivia-thumb library-strip-item';
        item.dataset.libId = String(lib.id || '');
        item.setAttribute('role', 'button');
        item.tabIndex = 0;
        if (state.selectedLibraryId === lib.id) {
            item.classList.add('is-selected');
        }

        const tile = document.createElement('div');
        tile.className = 'history-tile library-strip-tile';
        _buildThumbGrid(tile, lib.previews || []);

        const badge = document.createElement('div');
        badge.className = 'history-date';
        badge.textContent = _libraryTileCapacityLabel(lib.tile_count);
        tile.appendChild(badge);

        if (lib.is_public) {
            const publicBadge = document.createElement('div');
            publicBadge.className = 'library-public-badge';
            publicBadge.textContent = '🌍';
            publicBadge.title = 'Public library';
            tile.appendChild(publicBadge);
        }

        const label = document.createElement('div');
        label.className = 'meta';
        const name = document.createElement('span');
        name.className = 'name';
        name.textContent = lib.name || '';
        name.title = name.textContent;
        label.appendChild(name);

        item.appendChild(tile);
        item.appendChild(label);
        _ensureLibraryDeleteControl(item, lib);
        const selectLibrary = () => selectCallback(lib.id);
        item.onclick = selectLibrary;
        item.onkeydown = (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectLibrary();
            }
        };
        grid.appendChild(item);
    });
}

export function updateLibraryStripItem(lib) {
    if (!lib || !lib.id) return;
    const grid = document.getElementById('libGrid');
    if (!grid) return;
    const item = grid.querySelector(`.library-strip-item[data-lib-id="${String(lib.id)}"]`);
    if (!item) return;
    _applyLibraryStripItemContent(item, lib);
}

export function setLibraryStripSelection(selectedLibraryId) {
    const grid = document.getElementById('libGrid');
    if (!grid) return;
    const selectedId = selectedLibraryId ? String(selectedLibraryId) : '';
    const items = grid.querySelectorAll('.library-strip-item[data-lib-id]');
    items.forEach((item) => {
        const isSelected = Boolean(selectedId) && item.dataset.libId === selectedId;
        item.classList.toggle('is-selected', isSelected);
    });
    _ensureCreateLibraryItem();
}

export function renderLibraryPreview(data, append = false, opts = {}) {
    const area = document.getElementById('libPreviewArea');
    const grid = document.getElementById('libPreviewGrid');
    const count = document.getElementById('libPreviewCount');
    const nameEl = document.getElementById('libPreviewLibraryName');
    if (!area || !grid || !count) return;

    const forceCollapsed = Boolean(opts && opts.forceCollapsed);
    const loading = Boolean(opts && opts.loading);
    const error = String((opts && opts.error) || '').trim();
    const tileCount = Math.max(0, Number((data && data.count) || 0));
    const images = Array.isArray(data && data.images) ? data.images : [];
    const hasTiles = tileCount > 0 || images.length > 0;

    count.innerText = _libraryTileCapacityLabel(tileCount);
    const selected = _getSelectedLibraryForPreview();
    if (nameEl) nameEl.textContent = selected && selected.name ? selected.name : 'Library';

    if (!hasTiles) {
        if (!append) grid.innerHTML = "";
        _setLibraryPreviewFeedback({ grid });
        area.classList.add('hidden');
        return;
    }

    _ensureLibraryPreviewPanelEvents();
    area.classList.remove('hidden');
    _renderLibraryPreviewCollapsedSummary({
        tileCount,
        images,
        loading,
        error,
    });

    if (!append) {
        if (forceCollapsed) {
            _setLibraryPreviewCollapsed(true);
        }
        grid.innerHTML = "";
        grid.style.display = "grid";
        grid.style.gridTemplateColumns = "repeat(auto-fill, minmax(110px, 1fr))";
        grid.style.gap = "10px";
    }

    _setLibraryPreviewFeedback({
        grid,
        tileCount,
        loading,
        error,
    });

    if (!images.length) {
        if (loading) {
            _renderLibraryPreviewGridMessage(grid, _previewStatusMessage({ tileCount, loading }), 'info');
        } else if (error) {
            _renderLibraryPreviewGridMessage(grid, _previewStatusMessage({ tileCount, error }), 'error');
        }
        return;
    }

    images.forEach(url => {
        const filename = url.split('/').pop();
        const displayName = filename.replace(/^[a-f0-9]{8}_/, '');

        const card = document.createElement('div');
        card.className = "lib-item";

        const img = document.createElement('img');
        img.src = url;
        img.loading = "lazy";

        const nameLabel = document.createElement('div');
        nameLabel.className = "name";
        nameLabel.innerText = displayName;
        nameLabel.title = displayName;

        const delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = "del-btn";
        delBtn.innerText = "✕";
        delBtn.setAttribute('aria-label', `Delete tile ${displayName}`);
        delBtn.onclick = async (e) => {
            e.stopPropagation();
            const ok = await openConfirmModal({
                title: 'Delete tile photo?',
                message: UI_COPY.library.deleteConfirm(displayName),
                confirmText: 'Delete',
                danger: true,
            });
            if (!ok) return;
            try {
                const payload = await API.deleteTile(state.selectedLibraryId, filename);
                card.remove();
                const current = Number.parseInt(String(count.innerText || '').replace(/[^\d].*$/, ''), 10) || 0;
                const nextCount = Math.max(0, current - 1);
                count.innerText = _libraryTileCapacityLabel(nextCount);
                const idx = (state.libraries || []).findIndex((lib) => String(lib.id || '') === String(state.selectedLibraryId || ''));
                if (idx >= 0) {
                    const prev = state.libraries[idx] || {};
                    const prevPreviews = Array.isArray(prev.previews) ? prev.previews : [];
                    const updated = {
                        ...prev,
                        tile_count: nextCount,
                        previews: prevPreviews.filter((src) => String(src || '') !== String(url || '')),
                    };
                    state.libraries[idx] = updated;
                    updateLibraryStripItem(updated);
                    document.dispatchEvent(new CustomEvent('library:selected-updated', { detail: { library: updated } }));
                }
                if (payload && payload.replay_warning) {
                    showToast('Replay warning', String(payload.replay_warning), 5200);
                }
            } catch (err) {
                showToast('Delete failed', UI_COPY.library.deleteFailed(err.message), 0);
            }
        };

        card.appendChild(delBtn);
        card.appendChild(img);
        card.appendChild(nameLabel);

        grid.appendChild(card);
    });
}

export function setLibraryPreviewLoadState({
    tileCount = 0,
    images = [],
    loading = false,
    error = '',
} = {}) {
    const area = document.getElementById('libPreviewArea');
    const grid = document.getElementById('libPreviewGrid');
    if (!area || !grid) return;

    const safeCount = Math.max(0, Number(tileCount || 0));
    if (safeCount <= 0) {
        _setLibraryPreviewFeedback({ grid });
        return;
    }

    _ensureLibraryPreviewPanelEvents();
    area.classList.remove('hidden');
    _renderLibraryPreviewCollapsedSummary({
        tileCount: safeCount,
        images,
        loading,
        error,
    });
    _setLibraryPreviewFeedback({
        grid,
        tileCount: safeCount,
        loading,
        error,
    });

    const hasGridImages = Boolean(grid.querySelector('.lib-item'));
    if (!hasGridImages) {
        if (loading) {
            _renderLibraryPreviewGridMessage(grid, _previewStatusMessage({ tileCount: safeCount, loading }), 'info');
        } else if (error) {
            _renderLibraryPreviewGridMessage(grid, _previewStatusMessage({ tileCount: safeCount, error }), 'error');
        }
    }
}

export function updateLibraryPreviewActions(deleteCallback, renameCallback, changePrivacyCallback) {
    const area = document.getElementById('libPreviewActions');
    if (!area) return;
    const lib = (state.libraries || []).find((item) => item.id === state.selectedLibraryId) || null;
    if (!lib) {
        area.classList.add('hidden');
        area.innerHTML = '';
        return;
    }

    const isOwner = Boolean(lib.is_owner);
    const isAdmin = Boolean(state.isAdmin);
    const actions = [];

    if (isOwner) {
        actions.push({
            label: 'Rename',
            danger: false,
            handler: async () => {
                const newName = await openTextPromptModal({
                    title: 'Rename library',
                    label: 'Library name',
                    value: lib.name || '',
                    placeholder: 'e.g., Summer 2024',
                    confirmText: 'Rename',
                });
                if (newName && typeof renameCallback === 'function') renameCallback(lib.id, newName);
            },
        });
        if (isAdmin) {
            actions.push({
                label: 'Privacy',
                danger: false,
                handler: async () => {
                    const choice = await openPrivacyModal(lib.name, lib.is_public);
                    if (choice === null || choice === undefined) return;
                    if (typeof changePrivacyCallback === 'function') changePrivacyCallback(lib.id, choice);
                },
            });
        }
    }

    if (!actions.length) {
        area.classList.add('hidden');
        area.innerHTML = '';
        return;
    }

    area.innerHTML = '';
    actions.forEach((action) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `library-action-btn${action.danger ? ' danger' : ''}`;
        btn.textContent = action.label;
        btn.onclick = (e) => {
            e.stopPropagation();
            action.handler();
        };
        area.appendChild(btn);
    });
    area.classList.remove('hidden');
}

export function updateUploadProgress(current, total) {
    const bar = document.getElementById('libUploadBar');
    const txt = document.getElementById('libUploadText');
    const area = document.getElementById('libUploadArea');
    const stopBtn = document.getElementById('libUploadStopBtn');
    if (total > 0 && current < total) {
        area.classList.remove('hidden');
        if (stopBtn) stopBtn.classList.remove('hidden');
        const pct = Math.round((current / total) * 100);
        bar.style.width = pct + "%";
        txt.innerText = UI_COPY.library.uploadProgress(current, total);
    } else {
        area.classList.add('hidden');
        if (stopBtn) stopBtn.classList.add('hidden');
        bar.style.width = "0%";
    }
}
